import {
  fetchCostRates,
  fetchCostsByStacks,
  fetchEnvironments,
  fetchStacks,
} from "@app/deploy";
import { getStackType } from "@app/deploy";
import { selectOrganizationSelectedId } from "@app/organizations";
import { useCompositeLoader, useSelector } from "@app/react";
import { createStackUrl } from "@app/routes";
import { type DeployStackRow, selectStacksForTable } from "@app/stack-table";
import { AppSidebarLayout } from "../layouts";
import {
  ButtonLink,
  Group,
  IconPlusCircle,
  SignBaaBanner,
  TitleBar,
} from "../shared";
import { costColDef } from "../shared/cost-estimate-tooltip";
import { DataTable, idColDef } from "../shared/datatable";
import {
  CurrencyCell,
  DataTableCell,
  DateTimeCell,
  StackCell,
} from "../shared/datatable/cells";
import type {
  DataTableCellRendererProps,
  DataTableColDef,
} from "../shared/datatable/datatable";

// ─── Shared column definitions ───────────────────────────────────────────────

const colDefs: Record<string, DataTableColDef<DeployStackRow>> = {
  name: {
    field: "name",
    headerName: "Name",
    initialWidth: 300,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployStackRow>) => (
      <StackCell id={data.id} />
    ),
  },
  id: {
    field: "id",
    ...idColDef,
    valueGetter: ({ data }) => Number.parseInt(data?.id || "0", 10),
  },
  region: {
    field: "region",
    headerName: "Region",
  },
  typeTitle: {
    field: "typeTitle",
    headerName: "Tenancy",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployStackRow>) => {
      const tooltip =
        data.typeTitle === "Shared"
          ? "For non-production use only. Does not support compliance frameworks or Aptible's SLA."
          : "For production workloads. Supports HIPAA and other compliance frameworks, includes VPN and VPC Peering.";
      return <DataTableCell label={data.typeTitle} tooltip={tooltip} />;
    },
  },
  envCount: {
    field: "envCount",
    headerName: "Environments",
  },
  appCount: {
    field: "appCount",
    headerName: "Apps",
  },
  dbCount: {
    field: "dbCount",
    headerName: "Databases",
  },
  createdAt: {
    field: "createdAt",
    headerName: "Created At (UTC)",
    initialHide: true,
    valueGetter: ({ data }) =>
      data && getStackType(data) === "shared" ? null : data?.createdAt,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployStackRow>) =>
      data && getStackType(data) === "shared" ? (
        <span>-</span>
      ) : (
        <DateTimeCell date={data.createdAt} />
      ),
  },
  cost: {
    field: "cost",
    ...costColDef,
    sort: "desc",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployStackRow>) => (
      <CurrencyCell value={data.cost} />
    ),
  },
};

const stackListColDefs = [
  colDefs.name,
  colDefs.id,
  colDefs.region,
  colDefs.typeTitle,
  colDefs.envCount,
  colDefs.appCount,
  colDefs.dbCount,
  colDefs.createdAt,
  colDefs.cost,
];

function StackList() {
  const orgId = useSelector(selectOrganizationSelectedId);
  const { isLoading, isInitialLoading } = useCompositeLoader([
    fetchStacks,
    fetchEnvironments,
    fetchCostRates({ orgId }),
    fetchCostsByStacks({ orgId }),
  ]);
  const stacks = useSelector((s) => selectStacksForTable(s));

  return (
    <Group size="xl">
      <Group variant="horizontal" className="items-center justify-between">
        <TitleBar description="Stacks represent the virtualized infrastructure where resources are deployed.">
          Stacks
        </TitleBar>
        <ButtonLink to={createStackUrl()}>
          <IconPlusCircle variant="sm" className="mr-2" /> New Dedicated Stack
        </ButtonLink>
      </Group>

      <DataTable<DeployStackRow>
        rowData={stacks}
        colDefs={stackListColDefs}
        syncQueryParams={true}
        isLoading={!stacks.length && (isLoading || isInitialLoading)}
      />
    </Group>
  );
}

export function StacksPage() {
  return (
    <AppSidebarLayout>
      <SignBaaBanner />
      <StackList />
    </AppSidebarLayout>
  );
}
